// React
import { useEffect, useRef, useCallback, useMemo } from "react";

// Router
import { useLocation } from "react-router-dom";

// Redux
import { useDispatch, useSelector } from "react-redux";
import { dashboardOptionsRequestThunk, dashboardFiltersRequestThunk } from "../../features/dashboard/dashboardThunks";
import {
  setCurrentDashboard,
  setFilterValues,
  setInitialized,
  resetDashboard,
  selectDashboardLoading,
  selectDashboards,
  selectCurrentDashboardId,
  selectCurrentDashboard,
  selectPanelsByDashboardId,
  selectFilterConfig,
  selectDashboardInitialized
} from "../../features/dashboard/dashboardSlice";
import {pushNotification, setUiError, selectUiError, setActiveNavItem} from "../../features/application/applicationSlice";

// Carbon Design System
import { Column, Grid, Tile } from "@carbon/react";
import { FaceDizzy } from "@carbon/icons-react";

// Components
import FilterContainer from "../../components/Filters/FilterContainer";

// Models
import { NotificationModel } from "../../models/Notification";

// Utils
import { chartChooser } from "../../components/Panels/utils";
import { formatDate } from "../../utils/dateUtils";
import {
  initializeFilterDefaults,
  applyCoherentDefaults
} from "../../components/Filters/utils";
import {
  getAllPanelConfigs,
} from "../../components/Panels/utils";
import {
  findTelemetryDashboard
} from "../../utils/dashboardUtils";
import { useTranslation } from "react-i18next";
import { normalizeLanguage } from "../../i18n";

let lastLoadedDashboardLanguage = null;

const Index = () => {
  const { t, i18n } = useTranslation();
  const dispatch = useDispatch();
  const location = useLocation();

  const dashboardLoading = useSelector(selectDashboardLoading);
  const dashboards = useSelector(selectDashboards);
  const currentDashboardId = useSelector(selectCurrentDashboardId);
  const currentDashboard = useSelector(selectCurrentDashboard);
  const panelsById = useSelector(selectPanelsByDashboardId);
  const filterConfig = useSelector(selectFilterConfig);
  const uiError = useSelector(selectUiError);
  const initialized = useSelector(selectDashboardInitialized);

  const prevDashboardIdRef = useRef(null);
  const prevUiErrorRef = useRef('');
  const prevRequestKeyRef = useRef(null);
  const activeLanguage = normalizeLanguage(
    i18n.resolvedLanguage || i18n.language
  );

  const isTelemetryPage = location.pathname === "/telemetry";
  const activeNavItem = isTelemetryPage ? "telemetry" : "dashboard";
  const requestKey = `${activeNavItem}:${activeLanguage}`;

  const dashboardList = useMemo(() => {
    return Object.values(dashboards);
  }, [dashboards]);

  const telemetryDashboard = useMemo(() => {
    return findTelemetryDashboard(dashboards);
  }, [dashboards]);

  const selValue = currentDashboardId || -1;
  const hasSelectionChanged = prevDashboardIdRef.current !== currentDashboardId;

  const updateUiError = useCallback((newError) => {
    if (prevUiErrorRef.current !== newError) {
      prevUiErrorRef.current = newError;
      dispatch(setUiError(newError));
    }
  }, [dispatch]);

  useEffect(() => {
    dispatch(setActiveNavItem({ item: activeNavItem }));
  }, [dispatch, activeNavItem]);

  const dispatchPanels = useCallback(() => {
    dispatch(dashboardOptionsRequestThunk())
      .unwrap()
      .then((e) => {
        lastLoadedDashboardLanguage = activeLanguage;
        dispatch(setInitialized(true));
        if (Array.isArray(e) && e.length === 0) {
          updateUiError("Dashboard");
        } else {
          updateUiError("");
        }
      })
      .catch((e) => {
        if (e?.name === "ConditionError") return;

        dispatch(
          pushNotification(
            new NotificationModel(
              t("dashboard.reloadErrorTitle"),
              t("dashboard.reloadErrorBody", { status: e.http_status_code || t("common.unknown") }),
              "error"
            ).toJson()
          )
        );
      });
  }, [activeLanguage, dispatch, updateUiError, t]);

  // Inicialización: cargar dashboards solo una vez
  useEffect(() => {
    if (dashboardLoading) return;

    const hasRequestChanged = prevRequestKeyRef.current !== requestKey;
    const hasAnyDashboardLoaded = dashboardList.length > 0;
    const hasLanguageChanged = lastLoadedDashboardLanguage !== activeLanguage;

    if (hasLanguageChanged && hasAnyDashboardLoaded) {
      lastLoadedDashboardLanguage = activeLanguage;
      prevRequestKeyRef.current = null;
      dispatch(setInitialized(false));
      dispatch(resetDashboard());
      return;
    }

    if (hasRequestChanged) {
      prevRequestKeyRef.current = requestKey;
      dispatchPanels();
    }
  }, [
    activeLanguage,
    dashboardLoading,
    dispatch,
    requestKey,
    dashboardList.length,
    dispatchPanels
  ]);

  // Seleccionar el dashboard correspondiente cuando se cargan
  useEffect(() => {
    if (dashboardList.length === 0) {
      if (initialized) {
        updateUiError("Dashboard");
      }

      return;
    }

    const targetDashboard = isTelemetryPage
      ? telemetryDashboard
      : dashboardList[0];

    if (!targetDashboard) {
      if (isTelemetryPage) {
        updateUiError("Dashboard");
      }

      return;
    }

    if (currentDashboardId !== targetDashboard.id) {
      dispatch(setCurrentDashboard(targetDashboard.id));
      prevDashboardIdRef.current = targetDashboard.id;
    }

    updateUiError('');
  }, [dashboardList, telemetryDashboard, isTelemetryPage, currentDashboardId, initialized, dispatch, updateUiError]);

  useEffect(() => {
    if (selValue > 0 && hasSelectionChanged) {
      prevDashboardIdRef.current = selValue;
    }
  }, [selValue, hasSelectionChanged]);

  useEffect(() => {
    const panelValues = Object.values(panelsById);
    const panelConfigs = getAllPanelConfigs(currentDashboard);

    const hasAnyPanelData = panelValues.some((panel) => panel?.data);
    const isAnyPanelLoading = panelValues.some((panel) => panel?.loading);

    if (hasAnyPanelData || panelConfigs.length > 0) {
      updateUiError('');
      return;
    }

    if (!isAnyPanelLoading && initialized) {
      updateUiError('Panel');
    }
  }, [
    panelsById,
    currentDashboard,
    initialized,
    updateUiError
  ]);

  useEffect(() => {
    const selectedDashboardId = selValue;

    if (selectedDashboardId > 0 && filterConfig.length === 0) {
      dispatch(dashboardFiltersRequestThunk({ dashboardId: selectedDashboardId }))
        .unwrap()
        .then((filters) => {
          const defaults = initializeFilterDefaults(filters, formatDate);
          const coherent = applyCoherentDefaults(defaults, filters);
          dispatch(setFilterValues({ dashboardId: selectedDashboardId, values: coherent }));
        })
        .catch(() => { });
    }
  }, [selValue, dispatch, filterConfig.length]);

  const panelConfigs = getAllPanelConfigs(currentDashboard);
  const compactPanelTypes = new Set(["marker", "counter_chart", "status_chart"]);

  return (
    <div id="dashboard-page" className="page-container dashboard">
      <div className="dashboard-inner">

        {/* ---------- FILTROS ---------- */}
        <FilterContainer />

        {/* ---------- PANELES ---------- */}
        <div className="panels-wrapper">
          <Grid fullWidth className="charts-container">
            {panelConfigs.map((panel) => {
              const isCompactPanel = compactPanelTypes.has(panel.chart_type || panel.type);
              const panelClassName = [
                "dashboard-panel",
                isCompactPanel ? "dashboard-panel--compact" : "dashboard-panel--chart",
              ].join(" ");

              return (
                <Column
                  key={`${activeLanguage}-${panel.name}-${panel.chart_type}#${panel.id}`}
                  lg={panel.size || "8"}
                  xlg={panel.size || "8"}
                  md={8}
                  sm={4}
                >
                  <Tile className={panelClassName}>
                    {chartChooser(panel)}
                  </Tile>
                </Column>
              );
            })}
          </Grid>
        </div>

        {/* ---------- MENSAJE DE ERROR GLOBAL ---------- */}
        <div className="error-wrapper">
          {uiError && initialized && (
            <div className="no-content-wrapper">
              <div className="no-content-wrapper-inner">
                <h3>{t("dashboard.noConfiguration", {
                  resource: t(uiError === "Panel" ? "dashboard.panelResource" : "dashboard.dashboardResource")
                })}</h3>
                <FaceDizzy size={32} />
              </div>
              <h4>{t("dashboard.configureInAdmin")}</h4>
            </div>
          )}
        </div>
      </div>

    </div>
  );
}

export default Index;
